/**
 * Channel Detail Load Policy
 *
 * Triggered when channel detail view is opened. Loads the default-values form
 * configuration for the channel.
 *
 * NOTE: Billing entities are NOT loaded here. They are kept fresh by
 * `channelBillingEntitiesSyncPolicy` (a live subscription). A one-shot load here
 * went stale — it captured an empty list before the in-page setup wizard created
 * an entity and never refreshed.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';
import { generateChannelFormStructure } from '@utils/channels';

export const createChannelDetailLoadPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channel-detail-load',

  shouldReact: ( prev, next ) => {
    // Trigger when detail view opens (mode changes from closed to view)
    if ( prev.channelDetail.mode.type === 'closed' && next.channelDetail.mode.type === 'view' ) return true;
    // Also trigger when channel data arrives while the view is already open.
    // Handles the page-refresh race: detail/open fires before the channels
    // snapshot, so onStateChange is a no-op the first time.
    if ( next.channelDetail.mode.type === 'view' ) {
      const integrationId = next.channelDetail.mode.integrationId;
      if ( !prev.salesChannels[integrationId] && !!next.salesChannels[integrationId]) return true;
    }
    return false;
  },

  onStateChange: async ({ next, ctx }) => {
    const { dispatch } = ctx;
    
    if ( next.channelDetail.mode.type !== 'view' ) return;
    
    const integrationId = next.channelDetail.mode.integrationId;
    const channel = next.salesChannels[integrationId];
    
    if ( !channel ) return;

    // Billing entities are loaded by `channelBillingEntitiesSyncPolicy` (subscription).

    // Load default values form structure
    dispatch(
      { type: 'integrations/channels/detail/loading/setDefaultValues', payload: { isLoading: true }},
      { source: 'policy' }
    );
    
    try {
      const structure = await generateChannelFormStructure(
        channel.connectorId,
        'es', // TODO: Get from i18n context
        backend,
        'defaultValues'
      );
      
      const defaultValuesData = channel.defaultPublicationValues
        ? { ...structure.emptyData, ...channel.defaultPublicationValues }
        : structure.emptyData;
      
      dispatch(
        { 
          type: 'integrations/channels/detail/defaultValues/setConfig',
          payload: {
            fieldConfig: structure.fieldConfig,
            data: defaultValuesData
          }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( 'Error loading default values:', error );
    } finally {
      dispatch(
        { type: 'integrations/channels/detail/loading/setDefaultValues', payload: { isLoading: false }},
        { source: 'policy' }
      );
    }
  }
});
